import { useState, useEffect } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from 'recharts';
import api from '../api';
import { useLang } from '../context/LangContext';

const MONTHS = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
const YEAR_COLORS = ['#0d9488', '#2563eb', '#ea580c', '#9333ea', '#16a34a', '#dc2626'];

// rows [{year, month(1-12), energy_type, consumption, cost}] → [{month:'jan', 2024:.., 2025:..}]
function pivotByYear(rows, energyType, metric) {
  const base = MONTHS.map((m, i) => ({ month: m, _m: i + 1 }));
  rows.filter(r => r.energy_type === energyType).forEach(r => {
    const row = base[Number(r.month) - 1];
    if (row) row[r.year] = r[metric] == null ? null : Number(r[metric]);
  });
  return base;
}

export default function AnalyticsPage() {
  const { t } = useLang();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType]       = useState('elektriciteit');
  const [metric, setMetric]   = useState('consumption');

  useEffect(() => {
    api.get('/api/analytics/comparison')
      .then(r => setRows(r.data))
      .finally(() => setLoading(false));
  }, []);

  const years = Array.from(new Set(rows.map(r => Number(r.year)))).sort();
  const data = pivotByYear(rows, type, metric);
  const hasData = rows.some(r => r.energy_type === type);

  return (
    <>
      <div className="page-header"><h2>{t('analytics_title')}</h2></div>
      <div className="page-body">
        <div className="d-flex gap-2 mb-3 flex-wrap">
          <div className="btn-group btn-group-sm">
            <button className={`btn btn-outline-primary${type === 'elektriciteit' ? ' active' : ''}`}
              onClick={() => setType('elektriciteit')}>{t('type_elektriciteit')}</button>
            <button className={`btn btn-outline-primary${type === 'gas' ? ' active' : ''}`}
              onClick={() => setType('gas')}>{t('type_gas')}</button>
          </div>
          <div className="btn-group btn-group-sm">
            <button className={`btn btn-outline-secondary${metric === 'consumption' ? ' active' : ''}`}
              onClick={() => setMetric('consumption')}>{t('an_consumption')}</button>
            <button className={`btn btn-outline-secondary${metric === 'cost' ? ' active' : ''}`}
              onClick={() => setMetric('cost')}>{t('inv_amount')}</button>
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-title">
            {t('an_comparison')} — {t(`type_${type}`)} ·{' '}
            {metric === 'consumption'
              ? (type === 'gas' ? 'm³' : 'kWh')
              : '€ (incl. btw)'}
          </div>
          <div style={{ padding: '12px 12px 4px' }}>
            {loading ? (
              <div className="text-center text-muted py-5">
                <div className="spinner-border spinner-border-sm me-2" />{t('loading')}
              </div>
            ) : !hasData ? (
              <div className="text-center text-muted py-5">{t('no_data')}</div>
            ) : (
              <ResponsiveContainer width="100%" height={360}>
                <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="month" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip />
                  <Legend />
                  {years.map((y, i) => (
                    <Line key={y} type="monotone" dataKey={y} name={String(y)}
                      stroke={YEAR_COLORS[i % YEAR_COLORS.length]} dot={false} strokeWidth={2} connectNulls />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
